/**
 * iter142 — Reusable Studio Logo component.
 *
 * Renders the studio logo from useBranding(). If no logo is configured
 * OR the image fails to load, falls back to a text lockup showing the
 * studio name in the same serif brand font as the proposal hero.
 *
 * Props:
 *   size:    height in px (default 72). Width auto to preserve aspect ratio.
 *   variant: "auto" (default), "dark", or "light".
 *              - "dark"  → uses `logo_dark_url` if set (black on ivory bg)
 *              - "light" → uses `logo_light_url` if set (ivory on dark bg)
 *              - "auto"  → falls back to `logo_url`
 *   className / style: passthrough.
 *
 * Never stretches — always object-contain, aspect ratio preserved.
 * Crisp on Retina — since the source is a scalable webp/svg data-url or
 * a hi-res raster served at 2× the display size, browsers auto-resample.
 */
import { useBranding } from "@/lib/branding";

export default function StudioLogo({
  size = 72,
  variant = "auto",
  className = "",
  style = {},
  showFallbackText = true,
  fallbackTextClassName = "font-serif text-[#3b2f24] tracking-[0.15em]",
  fallbackTextStyle = { fontFamily: "'Playfair Display', Georgia, serif" },
  "data-testid": dataTestId = "studio-logo",
}) {
  const b = useBranding();
  const src = (() => {
    if (variant === "dark" && b.logo_dark_url) return b.logo_dark_url;
    if (variant === "light" && b.logo_light_url) return b.logo_light_url;
    return b.logo_url || b.logo_dark_url || "";
  })();

  if (!src) {
    if (!showFallbackText) return null;
    return (
      <div
        className={`inline-flex items-center justify-center ${fallbackTextClassName} ${className}`}
        style={{ fontSize: Math.round(size * 0.42), ...fallbackTextStyle, ...style }}
        data-testid={`${dataTestId}-fallback`}
      >
        {b.studio_name}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={b.studio_name || "Studio logo"}
      className={`object-contain ${className}`}
      style={{
        height: size,
        width: "auto",
        maxWidth: "100%",
        display: "inline-block",
        ...style,
      }}
      data-testid={dataTestId}
      onError={(e) => {
        // Hide the broken img — the fallback re-renders on next state
        // update; for safety we swap in the text version inline.
        e.currentTarget.style.display = "none";
      }}
    />
  );
}
